import { LISTING_STATE_PUBLISHED, LISTING_UNIT_TYPES } from './types';
import { getOfferPriceDetails } from './offerPricing';

export const CART_STORAGE_KEY = 'opposhopCartItems';
export const CART_UPDATED_EVENT = 'opposhopCartUpdated';
export const CART_ITEM_TYPE_ACCEPTED_OFFER = 'acceptedOffer';

const hasWindow = () => typeof window !== 'undefined' && !!window.localStorage;

const toPositiveInteger = value => {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value > 0 ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return /^[1-9]\d*$/.test(trimmed) ? Number.parseInt(trimmed, 10) : null;
  }

  if (value && typeof value.toNumber === 'function') {
    return toPositiveInteger(value.toNumber());
  }

  if (value && typeof value.toString === 'function') {
    return toPositiveInteger(value.toString());
  }

  return null;
};

const toQuantity = quantity => toPositiveInteger(quantity) || 1;

const clampQuantity = (quantity, maxQuantity) => {
  const parsedQuantity = toQuantity(quantity);
  const parsedMax = toPositiveInteger(maxQuantity);
  return parsedMax ? Math.min(parsedQuantity, parsedMax) : parsedQuantity;
};

const normalizeString = value =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const normalizeUuid = value => normalizeString(value) || normalizeString(value?.uuid);

const normalizePrice = price => {
  if (!price) {
    return null;
  }

  const amount = Number.parseInt(price.amount, 10);
  const currency = normalizeString(price.currency);

  return Number.isInteger(amount) && currency ? { amount, currency } : null;
};

const normalizeLineTotal = price => normalizePrice(price);

const normalizeSelectedOptions = selectedOptions => {
  if (!selectedOptions) {
    return null;
  }

  if (Array.isArray(selectedOptions)) {
    return selectedOptions.length > 0 ? selectedOptions : null;
  }

  return typeof selectedOptions === 'object' ? selectedOptions : null;
};

const listingImageUrl = listing => {
  const firstImage = listing?.images?.[0];
  const variants = firstImage?.attributes?.variants || {};
  const firstVariantWithUrl = Object.values(variants).find(variant => variant?.url);

  return (
    variants['listing-card']?.url ||
    variants['listing-card-2x']?.url ||
    variants['square-small']?.url ||
    variants['square-small2x']?.url ||
    firstVariantWithUrl?.url ||
    null
  );
};

export const isListingAvailableForCart = (
  listing,
  { isInfiniteStock = false, allowMissingPrice = false } = {}
) => {
  if (!listing) {
    return true;
  }

  const state = listing?.attributes?.state;
  const price = listing?.attributes?.price;
  const stock = listing?.currentStock?.attributes?.quantity;

  return (
    state === LISTING_STATE_PUBLISHED &&
    (!!price || allowMissingPrice) &&
    (isInfiniteStock || (typeof stock === 'number' && stock > 0))
  );
};

export const cartItemFromListing = listing => {
  const listingId = listing?.id?.uuid;
  if (!listingId) {
    return {};
  }

  const author = listing?.author;
  const sellerId = author?.id?.uuid;
  const sellerDisplayName = author?.attributes?.profile?.displayName;
  const title = listing?.attributes?.title;
  const description = listing?.attributes?.description;
  const image = listingImageUrl(listing);
  const price = normalizePrice(listing?.attributes?.price);

  return {
    listingId,
    ...(normalizeString(title) ? { title: normalizeString(title) } : {}),
    ...(normalizeString(description) ? { description: normalizeString(description) } : {}),
    ...(image ? { image } : {}),
    ...(price ? { price } : {}),
    ...(sellerId ? { sellerId } : {}),
    ...(normalizeString(sellerDisplayName)
      ? { sellerDisplayName: normalizeString(sellerDisplayName) }
      : {}),
  };
};

const userDisplayName = user => {
  const profile = user?.attributes?.profile || {};
  return normalizeString(profile.displayName) || normalizeString(profile.abbreviatedName);
};

const cartItemKey = item => {
  const transactionId = normalizeUuid(item?.transactionId);
  const listingIdRaw = typeof item === 'string' ? item : item?.listingId;
  const listingId = normalizeUuid(listingIdRaw);
  return transactionId ? `transaction:${transactionId}` : listingId;
};

export const getCartItemKey = item => cartItemKey(item);

export const isAcceptedOfferCartItem = item =>
  item?.itemType === CART_ITEM_TYPE_ACCEPTED_OFFER && !!normalizeUuid(item?.transactionId);

const normalizeCartItem = item => {
  const listingIdRaw = typeof item === 'string' ? item : item?.listingId;
  const listingId = normalizeUuid(listingIdRaw);
  if (!listingId) {
    return null;
  }

  const price = normalizePrice(item?.price);
  const lineTotal = normalizeLineTotal(item?.lineTotal);
  const selectedOptions = normalizeSelectedOptions(item?.selectedOptions);
  const itemType = normalizeString(item?.itemType);
  const transactionId = normalizeUuid(item?.transactionId);
  const transactionRole = normalizeString(item?.transactionRole);
  const customerId = normalizeUuid(item?.customerId);
  const providerId = normalizeUuid(item?.providerId);
  const otherPartyId = normalizeUuid(item?.otherPartyId);
  const offeredQuantity = toPositiveInteger(
    item?.offeredQuantity || item?.offerQuantity || item?.originalQuantity
  );

  return {
    listingId,
    quantity: toQuantity(item?.quantity),
    ...(offeredQuantity ? { offeredQuantity } : {}),
    ...(itemType ? { itemType } : {}),
    ...(transactionId ? { transactionId } : {}),
    ...(transactionRole ? { transactionRole } : {}),
    ...(item?.deliveryMethod ? { deliveryMethod: item.deliveryMethod } : {}),
    ...(normalizeString(item?.title) ? { title: normalizeString(item.title) } : {}),
    ...(normalizeString(item?.description)
      ? { description: normalizeString(item.description) }
      : {}),
    ...(normalizeString(item?.image) ? { image: normalizeString(item.image) } : {}),
    ...(price ? { price } : {}),
    ...(lineTotal ? { lineTotal } : {}),
    ...(normalizeString(item?.sellerId) ? { sellerId: normalizeString(item.sellerId) } : {}),
    ...(normalizeString(item?.sellerDisplayName)
      ? { sellerDisplayName: normalizeString(item.sellerDisplayName) }
      : {}),
    ...(customerId ? { customerId } : {}),
    ...(normalizeString(item?.customerDisplayName)
      ? { customerDisplayName: normalizeString(item.customerDisplayName) }
      : {}),
    ...(providerId ? { providerId } : {}),
    ...(normalizeString(item?.providerDisplayName)
      ? { providerDisplayName: normalizeString(item.providerDisplayName) }
      : {}),
    ...(otherPartyId ? { otherPartyId } : {}),
    ...(normalizeString(item?.otherPartyDisplayName)
      ? { otherPartyDisplayName: normalizeString(item.otherPartyDisplayName) }
      : {}),
    ...(item?.isInfiniteStock === true ? { isInfiniteStock: true } : {}),
    ...(selectedOptions ? { selectedOptions } : {}),
  };
};

const itemQuantity = item => toQuantity(item?.stockCount || item?.quantity);

const moneyToPlain = price => normalizePrice(price);

export const cartItemForProtectedData = item => {
  const quantity = itemQuantity(item);
  const offeredQuantity = toPositiveInteger(item?.offeredQuantity);
  const price = moneyToPlain(item?.price);
  const lineTotal = moneyToPlain(item?.lineTotal);
  const fallbackLineTotal = price
    ? {
        amount: price.amount * quantity,
        currency: price.currency,
      }
    : null;

  return {
    ...(normalizeString(item?.cartItemId) ? { cartItemId: normalizeString(item.cartItemId) } : {}),
    ...(normalizeUuid(item?.listingId) ? { listingId: normalizeUuid(item.listingId) } : {}),
    ...(normalizeUuid(item?.transactionId)
      ? { transactionId: normalizeUuid(item.transactionId) }
      : {}),
    ...(normalizeString(item?.itemType) ? { itemType: normalizeString(item.itemType) } : {}),
    ...(normalizeString(item?.title) ? { title: normalizeString(item.title) } : {}),
    ...(normalizeString(item?.description)
      ? { description: normalizeString(item.description) }
      : {}),
    quantity,
    ...(offeredQuantity ? { offeredQuantity } : {}),
    ...(normalizeString(item?.deliveryMethod)
      ? { deliveryMethod: normalizeString(item.deliveryMethod) }
      : {}),
    ...(normalizeUuid(item?.authorId) ? { authorId: normalizeUuid(item.authorId) } : {}),
    ...(normalizeUuid(item?.sellerId) ? { sellerId: normalizeUuid(item.sellerId) } : {}),
    ...(normalizeString(item?.sellerDisplayName)
      ? { sellerDisplayName: normalizeString(item.sellerDisplayName) }
      : {}),
    ...(normalizeUuid(item?.providerId) ? { providerId: normalizeUuid(item.providerId) } : {}),
    ...(normalizeString(item?.providerDisplayName)
      ? { providerDisplayName: normalizeString(item.providerDisplayName) }
      : {}),
    ...(normalizeUuid(item?.customerId) ? { customerId: normalizeUuid(item.customerId) } : {}),
    ...(normalizeString(item?.customerDisplayName)
      ? { customerDisplayName: normalizeString(item.customerDisplayName) }
      : {}),
    ...(normalizeUuid(item?.otherPartyId)
      ? { otherPartyId: normalizeUuid(item.otherPartyId) }
      : {}),
    ...(normalizeString(item?.otherPartyDisplayName)
      ? { otherPartyDisplayName: normalizeString(item.otherPartyDisplayName) }
      : {}),
    ...(normalizeString(item?.currency) || price?.currency
      ? { currency: normalizeString(item?.currency) || price.currency }
      : {}),
    ...(price ? { price } : {}),
    ...(lineTotal || fallbackLineTotal ? { lineTotal: lineTotal || fallbackLineTotal } : {}),
    ...(normalizeSelectedOptions(item?.selectedOptions)
      ? { selectedOptions: normalizeSelectedOptions(item.selectedOptions) }
      : {}),
  };
};

export const cartItemsForProtectedData = cartItems =>
  (Array.isArray(cartItems) ? cartItems : []).map(cartItemForProtectedData);

const parseProtectedCartItems = protectedData => {
  if (Array.isArray(protectedData?.cartCheckoutItems)) {
    return protectedData.cartCheckoutItems;
  }

  if (typeof protectedData?.cartItems === 'string') {
    try {
      const parsed = JSON.parse(protectedData.cartItems);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  return [];
};

export const cartItemsFromProtectedData = protectedData => {
  return parseProtectedCartItems(protectedData)
    .map(item => {
      const listingId = normalizeUuid(item?.listingId);
      if (!listingId) {
        return null;
      }

      const quantity = itemQuantity(item);
      const price = moneyToPlain(item?.price);
      const lineTotal = moneyToPlain(item?.lineTotal);

      return {
        ...item,
        listingId,
        quantity,
        stockCount: quantity,
        ...(price ? { price } : {}),
        ...(lineTotal ? { lineTotal } : {}),
      };
    })
    .filter(Boolean);
};

const uniqueCartItems = items => {
  const byCartItemKey = new Map();
  (Array.isArray(items) ? items : []).forEach(item => {
    const normalized = normalizeCartItem(item);
    const key = cartItemKey(normalized);
    if (normalized && key) {
      const existing = byCartItemKey.get(key);
      byCartItemKey.set(key, {
        ...existing,
        ...normalized,
        quantity: normalized.quantity,
      });
    }
  });
  return Array.from(byCartItemKey.values());
};

const notifyCartUpdated = cartItems => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT, { detail: { cartItems } }));
  }
};

export const readCartItems = () => {
  if (!hasWindow()) {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return uniqueCartItems(parsed);
  } catch (e) {
    return [];
  }
};

export const writeCartItems = items => {
  const cartItems = uniqueCartItems(items);
  if (hasWindow()) {
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    notifyCartUpdated(cartItems);
  }
  return cartItems;
};

export const isListingInCart = (listingId, items = readCartItems()) => {
  const id = normalizeUuid(listingId);
  return !!id && items.some(item => !isAcceptedOfferCartItem(item) && item.listingId === id);
};

export const isAcceptedOfferInCart = (transactionId, items = readCartItems()) => {
  const id = normalizeUuid(transactionId);
  return !!id && items.some(item => isAcceptedOfferCartItem(item) && item.transactionId === id);
};

const acceptedOfferLineItem = transaction => {
  const lineItems = transaction?.attributes?.lineItems || [];
  return lineItems.find(item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal);
};

export const cartItemFromAcceptedOffer = ({ transaction, listing, transactionRole }) => {
  const transactionId = normalizeUuid(transaction?.id);
  const listingSnapshot = cartItemFromListing(listing);
  const listingId = listingSnapshot.listingId || normalizeUuid(transaction?.listing?.id);
  const customer = transaction?.customer;
  const provider = transaction?.provider;
  const customerId = normalizeUuid(customer?.id);
  const providerId = normalizeUuid(provider?.id);
  const isCustomerRole = transactionRole === 'customer';
  const isProviderRole = transactionRole === 'provider';
  const otherParty = isCustomerRole ? provider : isProviderRole ? customer : null;
  const otherPartyId = normalizeUuid(otherParty?.id);
  const offerLineItem = acceptedOfferLineItem(transaction);
  const offerPriceDetails = getOfferPriceDetails({ transaction, listing });
  const price = normalizePrice(
    offerPriceDetails.unitPrice ||
      offerLineItem?.unitPrice ||
      offerLineItem?.lineTotal ||
      transaction?.attributes?.payinTotal ||
      listing?.attributes?.price
  );
  const lineTotal = normalizeLineTotal(
    offerPriceDetails.totalPrice || offerLineItem?.lineTotal || transaction?.attributes?.payinTotal
  );

  if (!transactionId || !listingId || !otherPartyId || !price) {
    return {};
  }

  const otherPartyDisplayName = userDisplayName(otherParty);
  const providerDisplayName = userDisplayName(provider);
  const customerDisplayName = userDisplayName(customer);
  const quantity = offerPriceDetails.quantity || toQuantity(offerLineItem?.quantity);

  return {
    ...listingSnapshot,
    itemType: CART_ITEM_TYPE_ACCEPTED_OFFER,
    listingId,
    transactionId,
    transactionRole,
    quantity,
    offeredQuantity: quantity,
    price,
    ...(lineTotal ? { lineTotal } : {}),
    ...(providerId ? { providerId } : {}),
    ...(providerDisplayName ? { providerDisplayName } : {}),
    ...(customerId ? { customerId } : {}),
    ...(customerDisplayName ? { customerDisplayName } : {}),
    otherPartyId,
    ...(otherPartyDisplayName ? { otherPartyDisplayName } : {}),
    sellerId: otherPartyId,
    ...(otherPartyDisplayName ? { sellerDisplayName: otherPartyDisplayName } : {}),
  };
};

export const addCartItem = ({
  listingId,
  listing,
  quantity = 1,
  deliveryMethod,
  maxQuantity,
  selectedOptions,
  title,
  image,
  price,
  sellerId,
  sellerDisplayName,
  isInfiniteStock = false,
  allowMissingPrice = false,
}) => {
  if (!isListingAvailableForCart(listing, { isInfiniteStock, allowMissingPrice })) {
    return readCartItems();
  }

  const cartItems = readCartItems();
  const listingSnapshot = cartItemFromListing(listing);
  const id = normalizeUuid(listingId) || listingSnapshot.listingId;
  const existing = cartItems.find(item => !isAcceptedOfferCartItem(item) && item.listingId === id);
  const quantityToAdd = toQuantity(quantity);
  const cap = Number.isInteger(maxQuantity) && maxQuantity > 0 ? maxQuantity : null;
  const nextQuantity = existing ? existing.quantity + quantityToAdd : quantityToAdd;
  const cappedQuantity = cap ? Math.min(nextQuantity, cap) : nextQuantity;

  const nextItem = normalizeCartItem({
    ...existing,
    ...listingSnapshot,
    listingId: id,
    title: title || listingSnapshot.title || existing?.title,
    image: image || listingSnapshot.image || existing?.image,
    price: price || listingSnapshot.price || existing?.price,
    sellerId: sellerId || listingSnapshot.sellerId || existing?.sellerId,
    sellerDisplayName:
      sellerDisplayName || listingSnapshot.sellerDisplayName || existing?.sellerDisplayName,
    isInfiniteStock: isInfiniteStock || existing?.isInfiniteStock === true,
    quantity: cappedQuantity,
    ...(deliveryMethod
      ? { deliveryMethod }
      : existing?.deliveryMethod
      ? { deliveryMethod: existing.deliveryMethod }
      : {}),
    ...(selectedOptions || existing?.selectedOptions
      ? { selectedOptions: selectedOptions || existing.selectedOptions }
      : {}),
  });

  return nextItem
    ? writeCartItems([...cartItems.filter(item => cartItemKey(item) !== id), nextItem])
    : cartItems;
};

export const addAcceptedOfferCartItem = ({ transaction, listing, transactionRole }) => {
  const cartItems = readCartItems();
  const nextItem = normalizeCartItem(
    cartItemFromAcceptedOffer({ transaction, listing, transactionRole })
  );

  if (!nextItem || isAcceptedOfferInCart(nextItem.transactionId, cartItems)) {
    return cartItems;
  }

  return writeCartItems([...cartItems, nextItem]);
};

const cartItemMatchesId = (item, id) => {
  return cartItemKey(item) === id || (!isAcceptedOfferCartItem(item) && item.listingId === id);
};

export const updateCartItemQuantity = (cartItemId, quantity, options = {}) => {
  const id = normalizeString(cartItemId);
  const nextQuantity = clampQuantity(quantity, options.maxQuantity);
  return writeCartItems(
    readCartItems().map(item =>
      cartItemMatchesId(item, id) ? { ...item, quantity: nextQuantity } : item
    )
  );
};

export const removeCartItem = cartItemId => {
  const id = normalizeString(cartItemId);
  return writeCartItems(readCartItems().filter(item => !cartItemMatchesId(item, id)));
};

export const removeCartItems = cartItemIds => {
  const idsToRemove = new Set(Array.isArray(cartItemIds) ? cartItemIds : []);
  return writeCartItems(readCartItems().filter(item => !idsToRemove.has(cartItemKey(item))));
};

export const cartItemCount = items => {
  return uniqueCartItems(items).reduce((sum, item) => sum + toQuantity(item.quantity), 0);
};
